<template>
  <v-container>
    <v-card v-if="$_inspectionTargetSegments.length > 0">
      <v-card-text>
        <dl class="insepctor-definition-list">
          <dt>Timeline Segment IDs</dt>
          <dd>
            <span
              class="enumeration"
              v-for="(timelineSegmentId, timelineSegmentIdIdx) of selectedTimelineSegmentIds"
              v-bind:key="timelineSegmentIdIdx"
            >
              {{ timelineSegmentId.mergedId }}
            </span>
          </dd>
        </dl>
        <dl class="insepctor-definition-list">
          <dt>Segment Type</dt>
          <dd>{{ $_inspectionTargetSegmentsSingleDataType }}</dd>
        </dl>
        <div v-if="$_singleInspectionTargetTimelineSegmentId">
          <dl class="insepctor-definition-list">
            <dt>Data</dt>
            <dd id="segment-data-treeview" class="as-block">
              <v-treeview
                open-all
                v-bind:items="$_segmentDataTreeviewItems"
              >
              </v-treeview>
            </dd>
          </dl>
          <dl class="insepctor-definition-list">
            <dt>References</dt>
            <dd class="as-block">
              <v-container
                fluid pt-0
                v-if="$_inspectionTargetSegments.length === 1"
              >
                <v-row no-gutters>
                  <v-col
                    col="6"
                    v-for="(timelineSegmentIdsReferToOrFrom, referToOrFrom) in $_inspectionTargetTimelineSegmentIdsReferToOrFrom"
                    v-bind:key="referToOrFrom"
                  >
                    <v-card flat>
                      <v-subheader>
                        <span>{{ $_getTitleByReferToOrFrom(referToOrFrom) }}</span>
                        <v-spacer></v-spacer>
                        <v-btn
                          outlined text x-small fab
                          v-bind:disabled="Object.keys(timelineSegmentIdsReferToOrFrom).length === 0"
                          v-on:click="$_emitUnregisterAllReferences(referToOrFrom)"
                          v-on:keydown.stop
                        >
                          <v-icon>mdi-delete</v-icon>
                        </v-btn>
                        <v-btn
                          outlined text x-small fab
                          v-on:click="$_toggleSelectSegmentsCallbackRegistoration(referToOrFrom, $event)"
                          v-on:keydown.stop
                        >
                          <v-icon>mdi-playlist-plus</v-icon>
                        </v-btn>
                      </v-subheader>
                      <v-btn
                        text small tile
                        v-for="(timelineSegmentId, referenceId) in timelineSegmentIdsReferToOrFrom"
                        v-bind:key="referenceId"
                        v-on:click="selectTimelineSegmentAndSeek(timelineSegmentId)"
                        v-on:keydown.stop
                      >
                        {{ timelineSegmentId.mergedId }}
                      </v-btn>
                    </v-card>
                  </v-col>
                </v-row>
              </v-container>
            </dd>
          </dl>
        </div>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<style scoped>
#segment-data-treeview >>> .v-treeview-node__root {
  min-height: 0;
}

#segment-data-treeview
>>> .v-treeview-node:not(.v-treeview-node--leaf)
> .v-treeview-node__root
> .v-treeview-node__content
> .v-treeview-node__label:after {
  content: ':';
}

.attached-ref-class {
  background-color: #f03e1e !important;
}

.enumeration:not(:last-child):after {
  content: ', ';
}

dl.insepctor-definition-list dd,
dl.insepctor-definition-list dt {
  display: inline-block;
  vertical-align: top;
}

dl.insepctor-definition-list dd.as-block {
  display: block;
}

dl.insepctor-definition-list dd {
  margin-left: 10px;
}

dl.insepctor-definition-list dt {
  font-weight: bold;
}

dl.insepctor-definition-list dt:after {
  content: ':';
}
</style>

<script>
import TimeValue from '../../modules/TimeValue.js';
import referencePool, { Reference } from '../../modules/referencePool.js';
import EditorDefinition from '../../modules/EditorDefinition.js';
import { TimelineDataSet } from '../../modules/ScattData.js';

function generateEmptyTimelineSegmentIdsReferToOrFrom() {
  return {
    referTo: {},
    referFrom: {},
  };
}

const segmentDataTypeStringMixed = 'mixed';

class TreeviewItemGenerator {
  static name = 'TreeviewItemGenerator';

  constructor() {
    this.nextId = 1;
  }

  generate(name) {
    let id = this.nextId;
    ++this.nextId;
    return { id, name };
  }
}

export const TimelineSegmentInspectorDefinition = new EditorDefinition(
  'ScattInspector',
  'Inspector',
  (timelineDataSet) => {
    for (let timelineData of Object.values(timelineDataSet)) {
      if (Object.keys(timelineData.segments).length > 0) return true;
    }
    return false;
  },
);

export default {
  watch: {
    $_singleInspectionTargetTimelineSegmentId(singleInspectionTargetTimelineSegmentId) {
      if (this.$_singleInspectionTargetTimelineSegmentId === undefined) return;
      let singleInspectionTargetTimelineSegmentMergedId = singleInspectionTargetTimelineSegmentId.mergedId;
      if (!Object.keys(this.$data.$_timelineSegmentIdsReferToOrFrom).includes(singleInspectionTargetTimelineSegmentMergedId)) {
        this.$set(
          this.$data.$_timelineSegmentIdsReferToOrFrom,
          singleInspectionTargetTimelineSegmentMergedId,
          generateEmptyTimelineSegmentIdsReferToOrFrom(),
        );
      }
    },
  },

  props: {
    selectedTimelineSegmentIds: { type: Array },
    timelineDataSet:            { type: TimelineDataSet },
  },

  data() {
    return {
      $_registerSelectSegmentsCallbackButtonElement: undefined,
      $_lockedInspectionTargetTimelineSegmentId: undefined,
      $_timelineSegmentIdsReferToOrFrom: {},
      $_onReferencePoolRegisterCallbackId: undefined,
      $_onReferencePoolUnregisterCallbackId: undefined,
    };
  },

  created() {
    let allReferences = referencePool.getAllReferences();
    this.$_createTimelineSegmentIdCache(Object.keys(allReferences), Object.values(allReferences));
    this.$data.$_onReferencePoolRegisterCallbackId = referencePool.registerOnRegisterCallback(this.$_createTimelineSegmentIdCache);
    this.$data.$_onReferencePoolUnregisterCallbackId = referencePool.registerOnUnregisterCallback(this.$_deleteTimelineSegmentIdCache);
  },

  destroyed() {
    if (this.$_isSelectSegmentsCallbackRegistered) {
      this.$_unregisterSelectSegmentsCallback();
    }
    referencePool.unregisterOnRegisterCallback(this.$data.$_onReferencePoolRegisterCallbackId);
    referencePool.unregisterOnUnregisterCallback(this.$data.$_onReferencePoolUnregisterCallbackId);
  },

  computed: {
    $_segmentDataTreeviewItems() {
      {
        if (this.$_segmentDataTemp === undefined) return undefined;
        let treeViewItemGenerator = new TreeviewItemGenerator();
        return getChildNodes(treeViewItemGenerator, this.$_segmentDataTemp);
      }

      function getChildNodes(treeViewItemGenerator, data) {
        if (data instanceof TimeValue) {
          return [ treeViewItemGenerator.generate(`${data.toFormatString()}`) ];
        } else if (data instanceof Object) {
          return Object.entries(data).map(
            ([ subKey, subData ]) => {
              let childItem = treeViewItemGenerator.generate(subKey);
              childItem.children = getChildNodes(treeViewItemGenerator, subData);
              return childItem;
            }
          );
        } else if (data instanceof Array) {
          return data.map(([ subData, subDataIdx ]) => {
            if (data instanceof TimeValue) {
              return treeViewItemGenerator.generate(`(${subDataIdx}) ${subData.toFormatString()}`);
            } else if ((subData instanceof Object) || (subData instanceof Array)) {
              let childItem = treeViewItemGenerator.generate(`(${subDataIdx})`);
              childItem.children = getChildNodes(treeViewItemGenerator, subData);
              return childItem;
            } else {
              return treeViewItemGenerator.generate(`(${subDataIdx}) ${subData}`);
            }
          });
        } else {
          return [ treeViewItemGenerator.generate(`${data}`) ];
        }
      }
    },

    $_segmentDataTemp() {
      if (this.$_singleInspectionTargetTimelineSegmentId) {
        return this.$_inspectionTargetSegments[0];
      } else {
        if (this.$_inspectionTargetSegmentsSingleDataType === segmentDataTypeStringMixed) {
          return undefined;
        } else {
          let representativeTimelineSegmentId = this.$_inspectionTargetTimelineSegmentIds[0];
          let representativeTimelineDataId = representativeTimelineSegmentId.timelineDataId;
          let singleSegmentDataType = this.timelineDataSet[representativeTimelineDataId].segmentDataType;
          let emptySegmentData = singleSegmentDataType.generateEmpty(null, null);
          return emptySegmentData;
        }
      }
    },

    $_singleInspectionTargetTimelineSegmentId() {
      if (this.$data.$_lockedInspectionTargetTimelineSegmentId !== undefined) {
        return this.$data.$_lockedInspectionTargetTimelineSegmentId;
      } else {
        if (this.selectedTimelineSegmentIds.length !== 1) return undefined;
        return this.selectedTimelineSegmentIds[0];
      }
    },

    $_inspectionTargetTimelineSegmentIds() {
      if (this.$data.$_lockedInspectionTargetTimelineSegmentId !== undefined) {
        return [ this.$data.$_lockedInspectionTargetTimelineSegmentId ];
      } else {
        return this.selectedTimelineSegmentIds;
      }
    },

    $_inspectionTargetSegments() {
      let targetTimelineSegmentIds = this.$_inspectionTargetTimelineSegmentIds;
      return targetTimelineSegmentIds.map(timelineSegmentId => {
        let timelineDataId = timelineSegmentId.timelineDataId;
        let segmentId = timelineSegmentId.segmentId;
        return this.timelineDataSet[timelineDataId].segments[segmentId];
      });
    },

    $_inspectionTargetSegmentsSingleDataType() {
      let initialTimelineSegmentId = this.$_inspectionTargetTimelineSegmentIds[0];
      if (initialTimelineSegmentId.length === 0) return undefined;
      let initialTimelineDataId = initialTimelineSegmentId.timelineDataId;
      let singleSegmentDataType = this.timelineDataSet[initialTimelineDataId].segmentDataType;
      for (let timelineSegmentId of this.$_inspectionTargetTimelineSegmentIds) {
        let timelineDataId = timelineSegmentId.timelineDataId;
        let currentSegmentDataType = this.timelineDataSet[timelineDataId].segmentDataType;
        if (singleSegmentDataType !== currentSegmentDataType) return segmentDataTypeStringMixed;
      }
      return String(singleSegmentDataType);
    },

    $_isSelectSegmentsCallbackRegistered() { return (this.$data.$_registerSelectSegmentsCallbackButtonElement !== undefined); },

    $_inspectionTargetTimelineSegmentIdsReferToOrFrom() {
      if (this.$_singleInspectionTargetTimelineSegmentId === undefined) return undefined;
      let singleInspectionTargetTimelineSegmentMergedId = this.$_singleInspectionTargetTimelineSegmentId.mergedId;
      return this.$data.$_timelineSegmentIdsReferToOrFrom[singleInspectionTargetTimelineSegmentMergedId];
    },
  },

  inject: [
    'registerSelectSegmentsCallback',
    'unregisterSelectSegmentsCallback',
    'registerReferences',
    'unregisterReferences',
    'selectTimelineSegmentAndSeek',
  ],

  methods: {
    $_emitUnregisterAllReferences(referToOrFrom) {
      if (!this.$_singleInspectionTargetTimelineSegmentId) return;
      let singleInspectionTargetTimelineSegmentMergedId = this.$_singleInspectionTargetTimelineSegmentId.mergedId;
      let timelineSegmentIdsReferToOrFrom = this.$data.$_timelineSegmentIdsReferToOrFrom[singleInspectionTargetTimelineSegmentMergedId][referToOrFrom];
      let referenceIds = Object.keys(timelineSegmentIdsReferToOrFrom);
      this.unregisterReferences(...referenceIds);
    },

    $_toggleSelectSegmentsCallbackRegistoration(referToOrFrom, event) {
      let registerSelectSegmentsCallbackButtonElement = event.currentTarget;
      if (this.$data.$_registerSelectSegmentsCallbackButtonElement === registerSelectSegmentsCallbackButtonElement) {
        this.$_unregisterSelectSegmentsCallback();
      } else {
        if (this.$_isSelectSegmentsCallbackRegistered) {
          this.$_unregisterSelectSegmentsCallback();
        }
        this.$_registerSelectSegmentsCallback(referToOrFrom, registerSelectSegmentsCallbackButtonElement);
      }
    },

    $_unregisterSelectSegmentsCallback() {
      if (this.$data.$_registerSelectSegmentsCallbackButtonElement !== undefined) {
        this.$data.$_registerSelectSegmentsCallbackButtonElement.classList.remove('attached-ref-class');
        this.$data.$_registerSelectSegmentsCallbackButtonElement = undefined;
      }
      if (this.$data.$_lockedInspectionTargetTimelineSegmentId !== undefined) {
        this.selectTimelineSegmentAndSeek(this.$data.$_lockedInspectionTargetTimelineSegmentId);
        this.$data.$_lockedInspectionTargetTimelineSegmentId = undefined;
      }
      this.unregisterSelectSegmentsCallback();
    },

    $_registerSelectSegmentsCallback(referToOrFrom, registerSelectSegmentsCallbackButtonElement) {
      {
        if (!this.$_singleInspectionTargetTimelineSegmentId) return;
        this.$data.$_registerSelectSegmentsCallbackButtonElement = registerSelectSegmentsCallbackButtonElement;
        this.$data.$_registerSelectSegmentsCallbackButtonElement.classList.add('attached-ref-class');
        this.$data.$_lockedInspectionTargetTimelineSegmentId = this.$_singleInspectionTargetTimelineSegmentId;
        this.registerSelectSegmentsCallback((selectedTimelineSegmentIds) => {
          let allReferences = referencePool.getAllReferences();
          let unregisteringReferenceIds = [];
          let registeringReferences = [];
          for (let selectedTimelineSegmentId of selectedTimelineSegmentIds) {
            let foundReferenceId = undefined;
            for (let [ referenceId, reference ] of Object.entries(allReferences)) {
              switch (referToOrFrom) {
              case 'referTo':
                if (!reference.referTo.isSameAs(selectedTimelineSegmentId)) break;
                if (!reference.referFrom.isSameAs(this.$_singleInspectionTargetTimelineSegmentId)) break;
                foundReferenceId = referenceId;
                break;
              case 'referFrom':
                if (!reference.referFrom.isSameAs(selectedTimelineSegmentId)) break;
                if (!reference.referTo.isSameAs(this.$_singleInspectionTargetTimelineSegmentId)) break;
                foundReferenceId = referenceId;
                break;
              }
            }
            if (foundReferenceId !== undefined) {
              unregisteringReferenceIds.push(foundReferenceId);
            } else {
              let referFrom = undefined;
              let referTo = undefined;
              switch (referToOrFrom) {
              case 'referTo':
                referFrom = this.$_singleInspectionTargetTimelineSegmentId;
                referTo = selectedTimelineSegmentId;
                break;
              case 'referFrom':
                referFrom = selectedTimelineSegmentId;
                referTo = this.$_singleInspectionTargetTimelineSegmentId;
                break;
              }
              let refClass = getRefClass.call(this, referFrom);
              registeringReferences.push(new Reference(refClass, referFrom, referTo));
            }
          }
          if (unregisteringReferenceIds.length > 0) {
            this.unregisterReferences(...unregisteringReferenceIds);
          }
          if (registeringReferences.length > 0) {
            this.registerReferences(...registeringReferences);
          }
        });
      }

      function getRefClass(timelineSegmentIdReferFrom) {
        let timelineDataId = timelineSegmentIdReferFrom.timelineDataId;
        return String(this.timelineDataSet[timelineDataId].segmentDataType);
      }
    },

    $_createTimelineSegmentIdCache(registeredReferenceIds, registeredReferences) {
      let numRegisteredReferences = registeredReferences.length;
      for (let registeredReferenceIdx = 0; registeredReferenceIdx < numRegisteredReferences; ++registeredReferenceIdx) {
        let registeredReferenceId = registeredReferenceIds[registeredReferenceIdx];
        let registeredReference = registeredReferences[registeredReferenceIdx];
        let timelineSegmentMergedIdReferFrom = registeredReference.referFrom.mergedId;
        let timelineSegmentMergedIdReferTo = registeredReference.referTo.mergedId;
        for (let timelineSegmentMergedId of [ timelineSegmentMergedIdReferFrom, timelineSegmentMergedIdReferTo ]) {
          if (!Object.keys(this.$data.$_timelineSegmentIdsReferToOrFrom).includes(timelineSegmentMergedId)) {
            this.$set(
              this.$data.$_timelineSegmentIdsReferToOrFrom,
              timelineSegmentMergedId,
              generateEmptyTimelineSegmentIdsReferToOrFrom(),
            );
          }
        }
        let timelineSegmentIdsReferFrom = this.$data.$_timelineSegmentIdsReferToOrFrom[timelineSegmentMergedIdReferFrom];
        this.$set(timelineSegmentIdsReferFrom.referTo, registeredReferenceId, registeredReference.referTo);
        let timelineSegmentIdsReferTo = this.$data.$_timelineSegmentIdsReferToOrFrom[timelineSegmentMergedIdReferTo];
        this.$set(timelineSegmentIdsReferTo.referFrom, registeredReferenceId, registeredReference.referFrom);
      }
    },

    $_deleteTimelineSegmentIdCache(unregisteredReferenceIds) {
      let numUnregisteredReferences = unregisteredReferenceIds.length;
      for (let unregisteredReferenceIdx = 0; unregisteredReferenceIdx < numUnregisteredReferences; ++unregisteredReferenceIdx) {
        let unregisteredReferenceId = unregisteredReferenceIds[unregisteredReferenceIdx];
        for (let timelineSegmentIds of Object.values(this.$data.$_timelineSegmentIdsReferToOrFrom)) {
          if (Object.keys(timelineSegmentIds.referFrom).includes(unregisteredReferenceId)) {
            this.$delete(timelineSegmentIds.referFrom, unregisteredReferenceId);
          }
          if (Object.keys(timelineSegmentIds.referTo).includes(unregisteredReferenceId)) {
            this.$delete(timelineSegmentIds.referTo, unregisteredReferenceId);
          }
        }
      }
    },

    $_getTitleByReferToOrFrom(referToOrFrom) {
      switch (referToOrFrom) {
      case 'referTo':
        return 'Refer To';
      case 'referFrom':
        return 'Refer From';
      }
    }
  },
};
</script>

